import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Pencil, Trash2, Shield } from 'lucide-react';
import { toast } from 'sonner';
import GmailIntegrationCard from '@/components/GmailIntegrationCard';
import AdminNotificationSettingsCard from '@/components/AdminNotificationSettingsCard';

const ROLES = { admin: 'Administrateur', employee: 'Employé', subcontractor: 'Sous-traitant', commercial: 'Commercial', client: 'Client' };
const ROLE_COLORS = {
  admin: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  employee: 'bg-sky-500/10 text-sky-400 border-sky-500/20',
  subcontractor: 'bg-violet-500/10 text-violet-400 border-violet-500/20',
};

export default function SettingsPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({ email: '', password: '', name: '', role: 'employee', phone: '', company: '' });

  const fetchUsers = async () => {
    try {
      const { data } = await api.get('/users');
      setUsers(data);
    } catch { toast.error('Erreur'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleSave = async () => {
    try {
      if (editingUser) {
        const updateData = { name: form.name, role: form.role, phone: form.phone, company: form.company };
        if (form.password) updateData.password = form.password;
        await api.put(`/users/${editingUser._id}`, updateData);
        toast.success('Utilisateur mis a jour');
      } else {
        await api.post('/users', form);
        toast.success('Utilisateur cree');
      }
      setDialogOpen(false);
      setEditingUser(null);
      setForm({ email: '', password: '', name: '', role: 'employee', phone: '', company: '' });
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleEdit = (u) => {
    setEditingUser(u);
    setForm({ email: u.email, password: '', name: u.name || '', role: u.role, phone: u.phone || '', company: u.company || '' });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet utilisateur ?')) return;
    try { await api.delete(`/users/${id}`); toast.success('Utilisateur supprime'); fetchUsers(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="flex items-center justify-center h-64 text-zinc-500">
        <Shield className="w-6 h-6 mr-2" /> Acces reserve aux administrateurs
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-zinc-50 tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Paramètres</h1>
          <p className="text-sm text-zinc-500 mt-1">Intégrations & utilisateurs</p>
        </div>
        <Button data-testid="new-user-btn" onClick={() => { setEditingUser(null); setForm({ email: '', password: '', name: '', role: 'employee', phone: '', company: '' }); setDialogOpen(true); }}
          className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium">
          <Plus className="w-4 h-4 mr-2" /> Nouvel utilisateur
        </Button>
      </div>

      {/* Gmail integration */}
      <GmailIntegrationCard />

      {/* Admin notification preferences — choose which sub events also CC the admin */}
      <AdminNotificationSettingsCard />

      <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-800 hover:bg-transparent">
              <TableHead className="text-xs uppercase tracking-wider text-zinc-500 font-medium">Nom</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-zinc-500 font-medium">Email</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-zinc-500 font-medium">Role</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-zinc-500 font-medium">Statut</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-zinc-500 font-medium text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              [...Array(3)].map((_, i) => (
                <TableRow key={i} className="border-zinc-800/50">
                  {[...Array(5)].map((__, j) => (<TableCell key={j}><div className="h-4 nexus-shimmer rounded w-24" /></TableCell>))}
                </TableRow>
              ))
            ) : users.map((u) => (
              <TableRow key={u._id} className="border-zinc-800/50 nexus-table-row">
                <TableCell className="text-sm text-zinc-200 font-medium">{u.name || '-'}</TableCell>
                <TableCell className="text-sm text-zinc-400">{u.email}</TableCell>
                <TableCell>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-sm text-xs font-medium border ${ROLE_COLORS[u.role] || ROLE_COLORS.employee}`}>
                    {ROLES[u.role] || u.role}
                  </span>
                </TableCell>
                <TableCell>
                  <span className={`text-xs ${u.active !== false ? 'text-emerald-400' : 'text-zinc-500'}`}>
                    {u.active !== false ? 'Actif' : 'Inactif'}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button data-testid={`edit-user-${u._id}`} onClick={() => handleEdit(u)} className="p-1.5 text-zinc-500 hover:text-zinc-100 transition-colors">
                      <Pencil className="w-4 h-4" />
                    </button>
                    {u._id !== user._id && (
                      <button data-testid={`delete-user-${u._id}`} onClick={() => handleDelete(u._id)} className="p-1.5 text-zinc-500 hover:text-rose-400 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-md" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
              {editingUser ? "Modifier l'utilisateur" : 'Nouvel utilisateur'}
            </DialogTitle>
            <DialogDescription className="text-zinc-500 text-sm">
              {editingUser ? "Modifiez les informations de l'utilisateur." : 'Créez un nouvel utilisateur.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Nom</Label>
              <Input data-testid="form-user-name" value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Email</Label>
              <Input data-testid="form-user-email" value={form.email} onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                disabled={!!editingUser} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">{editingUser ? 'Nouveau mot de passe (laisser vide pour ne pas changer)' : 'Mot de passe'}</Label>
              <Input data-testid="form-user-password" type="password" value={form.password} onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400 text-sm">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm(f => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="form-user-role" className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {Object.entries(ROLES).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-sm">Annuler</Button>
            <Button data-testid="save-user-btn" onClick={handleSave} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium">
              {editingUser ? 'Mettre a jour' : 'Creer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
